import Vue from 'vue'
import Router from 'vue-router'
import OidcCallback from './views/oidc/OidcCallback.vue'
import OidcPopupCallback from './views/oidc/OidcPopupCallback.vue'
import OidcCallbackError from './views/oidc/OidcCallbackError.vue'
import { vuexOidcCreateRouterMiddleware } from 'vuex-oidc'
import store from '@/store/store'
import { roleValidator } from '@/services/roleValidator'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/oidc-callback', // Needs to match redirectUri in you oidcSettings
      name: 'oidcCallback',
      component: OidcCallback,
    },
    {
      path: '/oidc-popup-callback', // Needs to match popupRedirectUri in you oidcSettings
      name: 'oidcPopupCallback',
      component: OidcPopupCallback,
    },
    {
      path: '/oidc-callback-error', // Needs to match redirect_uri in you oidcSettings
      name: 'oidcCallbackError',
      component: OidcCallbackError,
      meta: {
        isPublic: true,
      },
    },
    {
      path: '/print',
      component: () => import('@/views/print/Index'),
      children: [
        {
          name: 'printOpenEnrollmentSummary',
          path: 'printOpenEnrollmentSummary/:openEnrollmentId/:groupId',
          component: () => import('@/views/print/PrintOpenEnrollmentSummary'),
          props: true,
        },
      ],
    },
    {
      path: '/pages',
      component: () => import('@/views/pages/Index'),
      children: [
        {
          name: 'Lock',
          path: 'lock',
          component: () => import('@/views/pages/Lock'),
        },
        {
          name: 'About',
          path: 'about',
          component: () => import('@/views/pages/About'),
          meta: {
            isPublic: true,
          },
        },
        {
          name: 'Logout',
          path: 'logout',
          component: () => import('@/views/pages/Logout'),
          meta: {
            isPublic: true,
          },
        },
        {
          name: 'Login',
          path: 'login',
          component: () => import('@/views/pages/Login'),
          meta: {
            isPublic: true,
          },
        },
        {
          name: 'Pricing',
          path: 'pricing',
          component: () => import('@/views/pages/Pricing'),
          meta: {
            isPublic: true,
          },
        },
        {
          name: 'Register',
          path: 'register',
          component: () => import('@/views/pages/Register'),
          meta: {
            isPublic: true,
          },
        },
      ],
    },
    {
      path: '/',
      component: () => import('@/views/dashboard/Index'),
      children: [
        // Dashboard
        {
          name: 'Dashboard',
          path: '',
          component: () => import('@/views/dashboard/Dashboard'),
        },
        // Open Enrollment
        {
          name: 'Open Enrollments',
          path: 'openEnrollments',
          component: () => import('@/views/dashboard/openEnrollments/OpenEnrollments'),
        },
        // Data Management
        {
          name: 'Data Management / Platforms',
          path: 'datamanagement/platforms',
          component: () => import('@/views/dashboard/datamanagement/Platforms'),
        },
        {
          name: 'Data Management / Benefit Admin Sites',
          path: 'datamanagement/benefit-admin-sites',
          component: () => import('@/views/dashboard/datamanagement/BenefitAdminSites'),
        },
        {
          name: 'Data Management / Brokers',
          path: 'datamanagement/brokers',
          component: () => import('@/views/dashboard/datamanagement/Brokers'),
        },
        {
          name: 'Data Management / Carriers',
          path: 'datamanagement/carriers',
          component: () => import('@/views/dashboard/datamanagement/Carriers'),
        },
        {
          name: 'Data Management / Groups',
          path: 'datamanagement/groups',
          component: () => import('@/views/dashboard/datamanagement/Groups'),
        },
        {
          name: 'Data Management / Products',
          path: 'datamanagement/products',
          component: () => import('@/views/dashboard/datamanagement/Products'),
        },
        {
          name: 'Data Management / Product Types',
          path: 'datamanagement/product-types',
          component: () => import('@/views/dashboard/datamanagement/ProductTypes'),
        },
        {
          name: 'Data Management / Dropdowns',
          path: 'datamanagement/dropdowns',
          component: () => import('@/views/dashboard/datamanagement/Dropdowns'),
        },
        {
          name: 'Data Management / Templates',
          path: 'datamanagement/templates',
          component: () => import('@/views/dashboard/datamanagement/ProductTemplates'),
        },
        {
          name: 'Data Management / Enrollers',
          path: 'datamanagement/enrollers',
          component: () => import('@/views/dashboard/datamanagement/Enrollers'),
        },
        // Pages
        {
          name: 'RTL',
          path: 'pages/rtl',
          component: () => import('@/views/dashboard/pages/Rtl'),
        },
        {
          name: 'User Profile',
          path: 'pages/user',
          component: () => import('@/views/dashboard/pages/UserProfile'),
        },
        {
          name: 'Timeline',
          path: 'pages/timeline',
          component: () => import('@/views/dashboard/pages/Timeline'),
        },
        // Components
        {
          name: 'Buttons',
          path: 'components/buttons',
          component: () => import('@/views/dashboard/component/Buttons'),
        },
        {
          name: 'Grid System',
          path: 'components/grid-system',
          component: () => import('@/views/dashboard/component/Grid'),
        },
        {
          name: 'Tabs',
          path: 'components/tabs',
          component: () => import('@/views/dashboard/component/Tabs'),
        },
        {
          name: 'Notifications',
          path: 'components/notifications',
          component: () => import('@/views/dashboard/component/Notifications'),
        },
        {
          name: 'Icons',
          path: 'components/icons',
          component: () => import('@/views/dashboard/component/Icons'),
        },
        {
          name: 'Typography',
          path: 'components/typography',
          component: () => import('@/views/dashboard/component/Typography'),
        },
        // Forms
        {
          name: 'Regular Forms',
          path: 'forms/regular',
          component: () => import('@/views/dashboard/forms/RegularForms'),
        },
        {
          name: 'Extended Forms',
          path: 'forms/extended',
          component: () => import('@/views/dashboard/forms/ExtendedForms'),
        },
        {
          name: 'Validation Forms',
          path: 'forms/validation',
          component: () => import('@/views/dashboard/forms/ValidationForms'),
        },
        {
          name: 'Wizard',
          path: 'forms/wizard',
          component: () => import('@/views/dashboard/forms/Wizard'),
        },
        // Tables
        {
          name: 'Regular Tables',
          path: 'tables/regular-tables',
          component: () => import('@/views/dashboard/tables/RegularTables'),
        },
        {
          name: 'Extended Tables',
          path: 'tables/extended-tables',
          component: () => import('@/views/dashboard/tables/ExtendedTables'),
        },
        {
          name: 'Data Tabels',
          path: 'tables/data-tables',
          component: () => import('@/views/dashboard/tables/DataTables'),
        },
        // Maps
        {
          name: 'Google Maps',
          path: 'maps/google-maps',
          component: () => import('@/views/dashboard/maps/GoogleMaps'),
        },
        {
          name: 'Full Screen Map',
          path: 'maps/full-screen-map',
          component: () => import('@/views/dashboard/maps/FullScreenMap'),
        },
        // Root level
        {
          name: 'Widgets',
          path: 'widgets',
          component: () => import('@/views/dashboard/Widgets'),
        },
        {
          name: 'Charts',
          path: 'charts',
          component: () => import('@/views/dashboard/Charts'),
        },
        {
          name: 'Calendar',
          path: 'calendar',
          component: () => import('@/views/dashboard/Calendar'),
        },
      ],
    },
    {
      path: '*',
      component: () => import('@/views/pages/Index'),
      children: [
        {
          name: '404 Error',
          path: '',
          component: () => import('@/views/pages/Error'),
          meta: {
            isPublic: true,
          },
        },
      ],
    },
  ],
})

router.beforeEach((to, from, next) => {
  const isPublic = ((to || {}).meta || {}).isPublic === true
  if (isPublic) {
    next()
  } else {
    let handledNext = false
    const profile = store.getters['oidcStore/oidcUser']
    if (profile) {
      if (!roleValidator(profile)) {
        handledNext = true
        next('/pages/about')
      }
    }
    if (!handledNext) {
      vuexOidcCreateRouterMiddleware(store, 'oidcStore')(to, from, next)
    }
  }
})

router.afterEach((to, next) => {
  const isPublic = ((to || {}).meta || {}).isPublic === true
  if (!isPublic) {
    const profile = store.getters['oidcStore/oidcUser']
    if (profile) {
      if (!roleValidator(profile)) {
        router.push('/pages/about') // .catch(err => {})
      }
    }
  }
})

export default router
